/**
 * Sozialpass — Zod-Schemas (OU-2a).
 *
 * Liegt in `/shared` (CLAUDE.md §26), damit das Lehrer-Formular
 * (react-hook-form + zodResolver) und die autoritative Backend-Route gegen EIN
 * Schema validieren.
 *
 * `workDate` ist das **Arbeitsdatum** — wann die Stunde geleistet wurde — und
 * darf bewusst vom Eintragsdatum abweichen (Kollege trägt Tage später nach).
 * Zukunfts-Daten werden serverseitig gegen `timeSource.now()` geprüft; hier
 * bleibt nur die Formatprüfung, weil `/shared` keine Zeitquelle kennt (§10).
 */
import { z } from 'zod';
import { SOZIALPASS_DESCRIPTION_MAX, SOZIALPASS_STATUS_VALUES } from './sozialpass.js';
const DateStr = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum muss im Format YYYY-MM-DD sein.')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Kein gültiges Datum.');
const Description = z
    .string()
    .trim()
    .min(1, 'Bitte eintragen, was geleistet wurde.')
    .max(SOZIALPASS_DESCRIPTION_MAX, `Beschreibung darf höchstens ${SOZIALPASS_DESCRIPTION_MAX} Zeichen lang sein.`);
/** Body von `POST /api/v1/sozialpass`. */
export const SozialpassEntryCreateSchema = z.object({
    studentId: z.number().int().positive(),
    workDate: DateStr,
    description: Description,
});
/** Body von `PATCH /api/v1/sozialpass/:id` — `studentId` ist nicht umhängbar. */
export const SozialpassEntryUpdateSchema = z.object({
    workDate: DateStr,
    description: Description,
});
/** Sortier-Schlüssel der Übersichts-Liste. */
export const SOZIALPASS_SORT_KEYS = ['name', 'klasse', 'geleistet', 'offen'];
/**
 * Querystring von `GET /api/v1/sozialpass/overview` (+ `/overview.csv`).
 * `status` filtert auf den berechneten Status, `q` sucht in Vor-/Nachname.
 */
export const SozialpassOverviewQuerySchema = z.object({
    klasse: z.string().trim().min(1).max(20).optional(),
    q: z.string().trim().min(1).max(100).optional(),
    status: z.enum(SOZIALPASS_STATUS_VALUES).optional(),
    /** Schuljahr-Label "YYYY/YY"; Default = laufendes Schuljahr. */
    schoolyear: z
        .string()
        .regex(/^\d{4}\/\d{2}$/, 'Schuljahr muss im Format YYYY/YY sein.')
        .optional(),
    sort: z.enum(SOZIALPASS_SORT_KEYS).optional(),
    dir: z.enum(['asc', 'desc']).optional(),
});
//# sourceMappingURL=sozialpass-validation.js.map